import { Controller } from "@hotwired/stimulus"
import { isNotAValue } from "web/services/helpers"

export default class FileDropController extends Controller {
  static targets = [
    "input",
    "dropZone",
    "filenameDisplay",
    "filenameRow",
    "errorMessage",
  ]
  static classes = ["dropZoneActive"]

  static values = {
    maximumSingleFileSize: { type: Number, default: 100 * 1024 * 1024 },
    maximumTotalFileSize: { type: Number, default: 1000 * 1024 * 1024 },
  }

  #selectedFiles = []

  connect() {
    this.boundDropEnter = this.#dropEnter.bind(this)
    this.boundDropLeft = this.#dropLeft.bind(this)

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.addEventListener("dragenter", this.boundDropEnter)
      this.dropZoneTarget.addEventListener("dragover", this.boundDropEnter)
      this.dropZoneTarget.addEventListener("dragleave", this.boundDropLeft)
      this.dropZoneTarget.addEventListener("dragend", this.boundDropLeft)
    }
  }

  disconnect() {
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.removeEventListener("dragenter", this.boundDropEnter)
      this.dropZoneTarget.removeEventListener("dragover", this.boundDropEnter)
      this.dropZoneTarget.removeEventListener("dragleave", this.boundDropLeft)
      this.dropZoneTarget.removeEventListener("dragend", this.boundDropLeft)
    }

    this.#selectedFiles = []
  }

  handleFileSelect(event) {
    const filesWithFullPath = Array.from(event.currentTarget.files).map(
      (file) => {
        const filePath = isNotAValue(file.webkitRelativePath)
          ? file.name
          : file.webkitRelativePath

        return new File([file], filePath, {
          type: file.type,
        })
      }
    )

    this.handleFiles(filesWithFullPath)
  }

  async handleFileDrop(event) {
    if (!event.dataTransfer.types.includes("Files")) return

    this.#toggleDropZoneActiveClasses(event.currentTarget, false)

    const fileEntries = await this.#getDroppedFileEntries(
      event.dataTransfer.items
    )
    const files = []

    for (const fileEntry of fileEntries) {
      files.push(await this.#getFileFromEntry(fileEntry))
    }

    this.handleFiles(files)
  }

  handleFiles(files) {
    if (this.inputTarget.disabled) return

    const acceptableFiles = this.#filterAcceptableFiles(files)

    if (files.length > 0 && acceptableFiles.length === 0) {
      this.#clearSelection()
      this.#toggleErrorMessage(true)
      return
    }

    if (acceptableFiles.length === 0) return

    this.#toggleErrorMessage(false)
    this.#setInputFiles(acceptableFiles)

    this.inputTarget.dispatchEvent(
      new CustomEvent("file-drop:file-drop-change", { bubbles: true })
    )
    this.#selectedFiles = acceptableFiles
  }

  clear(event) {
    event.preventDefault()
    this.#clearSelection()
    this.#toggleErrorMessage(false)
  }

  #dropEnter(event) {
    if (event.dataTransfer.types.includes("Files")) {
      this.#toggleDropZoneActiveClasses(event.currentTarget, true)
      event.preventDefault()
      event.stopPropagation()
    }
  }

  #dropLeft(event) {
    if (event.dataTransfer.types.includes("Files")) {
      this.#toggleDropZoneActiveClasses(event.currentTarget, false)
      event.preventDefault()
      event.stopPropagation()
    }
  }

  #toggleDropZoneActiveClasses(element, force) {
    if (this.inputTarget.disabled) return

    this.dropZoneActiveClasses.forEach((className) =>
      element.classList.toggle(className, force)
    )
  }

  async #getDroppedFileEntries(items) {
    const fileEntries = []
    const queue = []

    for (const item of items) {
      queue.push(item.webkitGetAsEntry())
    }

    while (queue.length > 0) {
      const entry = queue.shift()

      if (entry.isFile) {
        fileEntries.push(entry)
      } else if (entry.isDirectory) {
        queue.push(...(await this.#getEntriesFromFolder(entry.createReader())))
      }
    }

    return fileEntries
  }

  async #getEntriesFromFolder(reader) {
    const fileEntries = []
    let readEntries = await this.#readEntriesPromise(reader)

    while (readEntries.length > 0) {
      fileEntries.push(...readEntries)
      readEntries = await this.#readEntriesPromise(reader)
    }

    return fileEntries
  }

  async #readEntriesPromise(directoryReader) {
    try {
      return await new Promise((resolve, reject) => {
        directoryReader.readEntries(resolve, reject)
      })
    } catch (err) {
      console.error(`Error processing file: ${err}`)
    }
  }

  async #getFileFromEntry(fileEntry) {
    return new Promise((resolve, reject) => {
      fileEntry.file(
        (file) => {
          // Copy the file since we want the full path in the name
          //   - Chrome has a bug where the full folders doesn't come through with the file() call
          const newFile = new File([file], fileEntry.fullPath, {
            type: file.type,
          })
          resolve(newFile)
        },
        (err) => {
          console.error(`Error processing file: ${err.message}`)
          reject()
        }
      )
    })
  }

  #filterAcceptableFiles(files) {
    const dataTransfer = new DataTransfer()

    for (const file of files) {
      if (this.#isAcceptableFile(file)) {
        dataTransfer.items.add(file)
      }
    }

    return dataTransfer.files
  }

  #isAcceptableFile(file) {
    return (
      !!file &&
      file.size <= this.maximumSingleFileSizeValue &&
      this.#matchesAcceptAttribute(file)
    )
  }

  #matchesAcceptAttribute(file) {
    const accept = this.inputTarget.accept
    if (!accept) return true

    const tokens = accept
      .split(",")
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)

    if (tokens.length === 0) return true

    const fileName = file.name.toLowerCase()
    const fileType = (file.type || "").toLowerCase()
    const hasExtensionToken = tokens.some((token) => token.startsWith("."))

    const matched = tokens.some((token) => {
      if (token.startsWith(".")) return fileName.endsWith(token)
      if (token.endsWith("/*")) return fileType.startsWith(token.slice(0, -1))
      return fileType === token
    })

    return matched || (!fileType && !hasExtensionToken)
  }

  #clearSelection() {
    this.inputTarget.value = ""
    this.#selectedFiles = []
    this.#updateFilenameDisplay([])
  }

  #setInputFiles(files) {
    if (files.length === 0) return

    const filesToSet = this.#getFilesToSet(files)
    if (!this.#validateAllInputFiles(filesToSet)) return

    this.inputTarget.files = filesToSet
    this.#updateFilenameDisplay(filesToSet)
  }

  #getFilesToSet(files) {
    if (this.inputTarget.multiple) return files

    const singleFileList = new DataTransfer()
    singleFileList.items.add(files[0])
    return singleFileList.files
  }

  #validateAllInputFiles(files) {
    let totalSize = 0
    for (const file of files) {
      totalSize += file.size
    }

    return totalSize <= this.maximumTotalFileSizeValue
  }

  #updateFilenameDisplay(files) {
    if (this.hasFilenameDisplayTarget) {
      const names = Array.from(files).map((file) =>
        file.name.replace(/^\//, "")
      )
      this.filenameDisplayTarget.textContent = names.join(", ")
    }

    if (this.hasFilenameRowTarget) {
      this.filenameRowTarget.classList.toggle("hidden", files.length === 0)
    }
  }

  #toggleErrorMessage(show) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.toggle("hidden", !show)
    }
  }
}
